"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { ComponentType, ReactNode } from "react";
import { cn } from "@drpcrd/utils/shared";
import { Typography } from "../typography";
import { useSidebarContext } from "./sidebar-context";

type Props = {
  title: string;
  icon: ComponentType<{ className?: string }>;
  /** Renders as a link when given; renders as a button (calling `onClick`)
   * otherwise. */
  href?: string;
  onClick?: () => void;
  /** Defaults to matching the current pathname against `href` (exact match,
   * or a sub-route match for multi-segment paths) — pass explicitly to
   * override, e.g. for a tab that covers several routes. */
  isActive?: boolean;
  actionButton?: ReactNode;
};

const isPathActive = (pathname: string | null, href: string): boolean => {
  if (!pathname) return false;
  const normalizedPathname = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const normalizedHref = href.endsWith("/") ? href.slice(0, -1) : href;

  if (normalizedPathname === normalizedHref) return true;

  const isRootOrSingleLevel = normalizedHref === "" || !normalizedHref.includes("/", 1);
  return !isRootOrSingleLevel && normalizedPathname.startsWith(normalizedHref + "/");
};

/**
 * Single navigation row for `SidebarBody` — icon, label, and an optional
 * trailing action button, highlighting itself when active. On the desktop
 * collapsed rail (`useSidebarContext().isExpanded === false`), renders as an
 * icon-only tile instead — no separate `collapsedIcons` list needed, this
 * adapts itself. Inside the mobile slide-over, clicking closes the menu.
 */
export const SidebarNavItem = ({ title, icon: Icon, href, onClick, isActive, actionButton }: Props) => {
  const { slot, isExpanded, closeMobileMenu } = useSidebarContext();
  const pathname = usePathname();
  const active = isActive ?? (href ? isPathActive(pathname, href) : false);

  const handleClick = () => {
    onClick?.();
    if (slot === "mobile-menu") closeMobileMenu();
  };

  if (slot === "desktop" && !isExpanded) {
    const content = (
      <div
        className={cn(
          "group flex items-center justify-center rounded-md border border-transparent py-1.25 text-sm transition-colors",
          active ? "bg-primary-50 text-neutral-900" : "hover:bg-neutral-200",
        )}
        title={title}
      >
        <Icon
          className={cn(
            "h-2 w-2 shrink-0 transition-colors",
            active ? "text-black" : "text-neutral-900 group-hover:text-black",
          )}
        />
      </div>
    );
    return (
      <div className="w-full px-1">
        {href ? (
          <Link href={href} aria-label={title}>
            {content}
          </Link>
        ) : (
          <button onClick={handleClick} aria-label={title} className="w-full cursor-pointer">
            {content}
          </button>
        )}
      </div>
    );
  }

  const rowClassName = cn(
    "group flex items-center rounded-md border border-transparent px-1.5 py-1 text-sm transition-colors",
    active ? "bg-primary-50 text-neutral-900" : "hover:bg-neutral-200",
  );
  const rowContent = (
    <>
      <Icon
        className={cn(
          "mr-1.5 h-2 w-2 transition-colors",
          active ? "text-black" : "text-neutral-900 group-hover:text-black",
        )}
      />
      <Typography
        color={active ? "black" : "neutral"}
        size="sm"
        weight="semibold"
        className={cn("transition-colors", !active && "group-hover:text-black")}
      >
        {title}
      </Typography>
    </>
  );

  return (
    <div className="group/tab relative px-1">
      {href ? (
        <Link href={href} className={rowClassName} onClick={handleClick}>
          {rowContent}
        </Link>
      ) : (
        <button onClick={handleClick} className={cn(rowClassName, "w-full cursor-pointer text-left")}>
          {rowContent}
        </button>
      )}
      {actionButton && <div className="absolute top-1/2 right-2 -translate-y-1/2">{actionButton}</div>}
    </div>
  );
};
